/*
 * What turns an empty installation into a place a team can work in (VISUAL.md §7.16, D-10 §2.4):
 * four steps, each ticking itself off as it is done. On an instance whose only account is the
 * administrator's this list *is* the first screen (`FirstRunAdmin.tsx`); afterwards it stands on
 * Home for as long as the instance is new, and then takes itself away.
 *
 * It ticks from the model rather than from a stored flag, which is the whole point: a checklist with
 * its own state is a second truth about the instance and drifts from it within a day. Every row is a
 * question the client model can already answer — is there a channel, is there anybody else, has this
 * person written something, do they have a picture — so a step somebody did in another tab, or before
 * this screen existed, is already ticked when the screen opens.
 *
 * It disappears on its own: once every row it shows is ticked, and in any case once the reader is in
 * more than `MAX_CONVERSATIONS` conversations, because by then the instance is not new and Home has
 * better things to say. A row nobody can act on is not shown at all — only an administrator can
 * invite — so the list never ends in a door that refuses.
 *
 * Every row ends in a door. Core knows its own addresses (`/admin/people`, `/settings/profile`) and
 * is handed the conversation's one by `routes/frames.tsx`, which is the screen that already decides
 * which conversation a reader most wants; the one step whose control is not an address — creating
 * the channel everybody joins — is a module's component (`HomeSection.SetupDoor`), because core does
 * not know how a channel is made. Without a module offering one, the row names the control instead.
 */

import { Link } from "@tanstack/react-router"
import type { ComponentType } from "react"
import { cx } from "../../lib/cx.ts"
import { t, type PlainKey } from "../i18n/index.ts"
import { isAdmin } from "../people/standing.ts"
import type { Model } from "../store/model.ts"
import { useModel } from "./context.tsx"
import "./home.css"
import { Icon } from "./icons.tsx"

/** A conversation as a door offers it: where it is, and what it is called there (`#allgemein`). */
export interface PlaceDoor {
	href: string
	/** The place's own name — `core/store/conversationName.ts`'s address. */
	label: string
}

/** Above this many conversations the instance is no longer new, whatever the checklist still says. */
const MAX_CONVERSATIONS = 3

/** What the model answers about the state of the instance; one boolean per question a row asks. */
interface Progress {
	hasChannel: boolean
	hasTeam: boolean
	hasSpoken: boolean
	hasPicture: boolean
	isAdmin: boolean
}

interface Step {
	id: string
	labelKey: PlainKey
	done: (p: Progress) => boolean
	/** Left out on a step the reader cannot carry out; the row is then not drawn at all. */
	shown?: (p: Progress) => boolean
	/** Where the step is done, when there is an address for it. */
	door?: (channel: PlaceDoor | undefined) => PlaceDoor | undefined
	/** The control that does it, for a step with no address of its own and no module door. */
	hintKey?: PlainKey
	/** A step the place works without; the row says so in a word. */
	optional?: boolean
}

/**
 * The four steps, in the order they make sense: a place to talk, people to talk to, the first thing
 * said, and a face beside it.
 */
const STEPS: Step[] = [
	{
		id: "channel",
		labelKey: "shell.setup.channel",
		done: (p) => p.hasChannel,
		hintKey: "shell.setup.channel.hint",
	},
	{
		id: "invite",
		labelKey: "shell.setup.invite",
		done: (p) => p.hasTeam,
		shown: (p) => p.isAdmin,
		door: () => ({ href: "/admin/people", label: t("shell.setup.invite.action") }),
	},
	{
		id: "hello",
		labelKey: "shell.setup.hello",
		done: (p) => p.hasSpoken,
		door: (channel) =>
			channel === undefined
				? undefined
				: { href: channel.href, label: t("shell.home.open", { channel: channel.label }) },
		// On the administrator's first day there is no channel for this step to point at, so the hint
		// takes the door's place: the row of a list whose every other row ends in a control would
		// otherwise end in an empty column (N4-R2-11). It goes as soon as row 1 is done.
		hintKey: "shell.setup.hello.hint",
	},
	{
		id: "picture",
		labelKey: "shell.setup.picture",
		done: (p) => p.hasPicture,
		door: () => ({ href: "/settings/profile", label: t("shell.setup.picture.action") }),
		optional: true,
	},
]

/** True while the reader is in a channel of either kind; a direct conversation is not a place. */
function hasChannel(model: Model): boolean {
	return model.conversationOrder.some((id) => {
		const kind = model.conversations[id]?.meta.kind
		return kind === "channel" || kind === "private"
	})
}

/** True once the directory holds somebody else who can sign in — the bot account is not company. */
function hasTeam(model: Model): boolean {
	const meId = model.me?.id ?? 0
	return Object.values(model.users).some((user) => user.id !== meId && user.kind !== "bot")
}

/**
 * True once this person has written a message this client can see. It reads the loaded windows, so
 * the answer is "in what is open" rather than "ever": the step is ticked the moment the hello is sent
 * and stays ticked on the next load, because the conversation it was written in opens at its tail.
 * A count that were exact would be a server-side question, and the step is a nudge, not a statistic.
 */
function hasSpoken(model: Model): boolean {
	const meId = model.me?.id ?? 0
	return Object.values(model.messages).some((window) =>
		window.rows.some((row) => row.authorId === meId && row.deletedAt === undefined),
	)
}

function progressOf(model: Model): Progress {
	return {
		hasChannel: hasChannel(model),
		hasTeam: hasTeam(model),
		hasSpoken: hasSpoken(model),
		hasPicture: (model.me?.avatarUrl ?? "") !== "",
		isAdmin: isAdmin(model.me),
	}
}

/** The rows this reader is shown, and how many of them are done. */
function rowsOf(progress: Progress): { rows: Step[]; done: number } {
	const rows = STEPS.filter((step) => step.shown?.(progress) ?? true)
	return { rows, done: rows.filter((step) => step.done(progress)).length }
}

/**
 * Whether the instance is still being set up — the condition the checklist draws itself under, read
 * as one boolean so Home can choose the administrator's first screen by it (`routes/frames.tsx`).
 */
export function setupPending(model: Model): boolean {
	if (model.conversationOrder.length > MAX_CONVERSATIONS) return false
	const { rows, done } = rowsOf(progressOf(model))
	return done < rows.length
}

export function SetupChecklist({
	channel,
	SetupDoor,
	onDoor,
	headed = true,
}: {
	channel?: PlaceDoor | undefined
	/** The module's door for the first row (`HomeSection.SetupDoor`); absent, the row names the control. */
	SetupDoor?: ComponentType<{ onDone: () => void }> | undefined
	/** Run when a door is taken: the first-run screen dismisses itself on the way out. */
	onDoor?: (() => void) | undefined
	/** Whether the card carries its own heading; the first-run screen's `h1` says it already. */
	headed?: boolean
}) {
	// One primitive per question, so the card repaints when an answer changes and not otherwise.
	const pending = useModel(setupPending)
	const hasChannel = useModel((model) => progressOf(model).hasChannel)
	const hasTeam = useModel((model) => progressOf(model).hasTeam)
	const hasSpoken = useModel((model) => progressOf(model).hasSpoken)
	const hasPicture = useModel((model) => progressOf(model).hasPicture)
	const admin = useModel((model) => progressOf(model).isAdmin)
	if (!pending) return null

	const progress: Progress = { hasChannel, hasTeam, hasSpoken, hasPicture, isAdmin: admin }
	const { rows, done } = rowsOf(progress)
	const percent = Math.round((done / rows.length) * 100)

	return (
		<section className="tb-setup" aria-labelledby="tb-setup-title">
			<h2 className={cx("tb-setup__title", !headed && "tb-visually-hidden")} id="tb-setup-title">
				{t("shell.setup.title")}
			</h2>
			{/* The bar and the sentence are one fact said twice — once for the eye, once in words — so
			    the bar itself is out of the accessible tree and the sentence carries the number. */}
			<div className="tb-setup__bar" aria-hidden="true">
				<span className="tb-setup__fill" style={{ inlineSize: `${percent}%` }} />
			</div>
			<p className="tb-setup__progress">
				{t("shell.setup.progress", { done, total: rows.length })}
			</p>
			<ol className="tb-setup__steps">
				{rows.map((step, at) => {
					const ticked = step.done(progress)
					const door = ticked ? undefined : step.door?.(channel)
					return (
						<li key={step.id} className={cx("tb-setup__step", ticked && "tb-setup__step--done")}>
							{/* The ring is the empty state of the tick: one shape that either holds a mark or a
							    number. */}
							<span className="tb-setup__mark" aria-hidden="true">
								{ticked ? <Icon name="check" size={16} /> : at + 1}
							</span>
							<span className="tb-setup__what">
								{t(step.labelKey)}
								{step.optional === true ? (
									<span className="tb-setup__optional">{t("shell.setup.optional")}</span>
								) : null}
								{/* The tick is a picture; the state is a word, so a row reads as what it is. */}
								<span className="tb-visually-hidden">
									{` — ${ticked ? t("shell.setup.done") : t("shell.setup.todo")}`}
								</span>
							</span>
							{ticked ? null : step.id === "channel" && SetupDoor !== undefined ? (
								<SetupDoor onDone={() => onDoor?.()} />
							) : door !== undefined ? (
								<Link
									to={door.href}
									className="tb-button tb-button--secondary tb-button--sm"
									onClick={() => onDoor?.()}
								>
									{door.label}
								</Link>
							) : step.hintKey === undefined ? null : (
								<span className="tb-setup__hint">{t(step.hintKey)}</span>
							)}
						</li>
					)
				})}
			</ol>
		</section>
	)
}
